import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Target, Flame, TrendingUp, Edit3, Check, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

const WritingGoals = () => {
  const { api } = useAuth();
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [dailyGoal, setDailyGoal] = useState('');
  const [weeklyGoal, setWeeklyGoal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/writing-goals');
        setGoals(res.data);
        setDailyGoal(res.data.daily_goal || '');
        setWeeklyGoal(res.data.weekly_goal || '');
      } catch (e) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/writing-goals', {
        daily_goal: parseInt(dailyGoal) || 0,
        weekly_goal: parseInt(weeklyGoal) || 0
      });
      const res = await api.get('/writing-goals');
      setGoals(res.data);
      setEditing(false);
    } catch (e) {
      // Non-critical error suppressed
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const dailyPct = goals?.daily_goal > 0 ? Math.min(100, Math.round((goals.today_words / goals.daily_goal) * 100)) : 0;
  const weeklyPct = goals?.weekly_goal > 0 ? Math.min(100, Math.round((goals.week_words / goals.weekly_goal) * 100)) : 0;
  const hasGoals = (goals?.daily_goal > 0) || (goals?.weekly_goal > 0);

  return (
    <Card className="border-brand-primary/10" data-testid="writing-goals-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-semibold text-text-primary flex items-center gap-2">
            <Target className="w-5 h-5 text-brand-accent" />
            Writing Goals
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(!editing)}
            className="text-text-muted"
            data-testid="edit-goals-btn"
          >
            <Edit3 className="w-4 h-4" />
          </Button>
        </div>

        {editing ? (
          <div className="space-y-3" data-testid="goals-edit-form">
            <div>
              <label className="text-xs text-text-muted mb-1 block">Daily word goal</label>
              <Input
                type="number"
                value={dailyGoal}
                onChange={(e) => setDailyGoal(e.target.value)}
                placeholder="e.g. 500"
                className="h-9"
                data-testid="daily-goal-input"
              />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Weekly word goal</label>
              <Input
                type="number"
                value={weeklyGoal}
                onChange={(e) => setWeeklyGoal(e.target.value)}
                placeholder="e.g. 3000"
                className="h-9"
                data-testid="weekly-goal-input"
              />
            </div>
            <Button onClick={handleSave} disabled={saving} className="btn-accent w-full gap-2" data-testid="save-goals-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Goals
            </Button>
          </div>
        ) : hasGoals ? (
          <div className="space-y-4">
            {/* Streak */}
            {goals.streak > 0 && (
              <div className="flex items-center gap-2 text-sm" data-testid="writing-streak">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="font-medium text-text-primary">{goals.streak} day streak!</span>
              </div>
            )}

            {/* Daily progress */}
            {goals.daily_goal > 0 && (
              <div data-testid="daily-progress">
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-text-muted">Today</span>
                  <span className="font-medium text-text-primary">
                    {goals.today_words.toLocaleString()} / {goals.daily_goal.toLocaleString()}
                  </span>
                </div>
                <div className="h-2.5 bg-surface-alt rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${dailyPct >= 100 ? 'bg-green-500' : 'bg-brand-accent'}`}
                    style={{ width: `${dailyPct}%` }}
                  />
                </div>
                {dailyPct >= 100 && (
                  <p className="text-xs text-green-600 mt-1 font-medium">Daily goal reached!</p>
                )}
              </div>
            )}

            {/* Weekly progress */}
            {goals.weekly_goal > 0 && (
              <div data-testid="weekly-progress">
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-text-muted">This week</span>
                  <span className="font-medium text-text-primary">
                    {goals.week_words.toLocaleString()} / {goals.weekly_goal.toLocaleString()}
                  </span>
                </div>
                <div className="h-2.5 bg-surface-alt rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${weeklyPct >= 100 ? 'bg-green-500' : 'bg-brand-primary'}`}
                    style={{ width: `${weeklyPct}%` }}
                  />
                </div>
                {weeklyPct >= 100 && (
                  <p className="text-xs text-green-600 mt-1 font-medium">Weekly goal reached!</p>
                )}
              </div>
            )}

            {/* Mini history chart */}
            {goals.history && goals.history.length > 0 && (
              <div data-testid="writing-history">
                <p className="text-xs text-text-muted mb-2 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Last 30 days
                </p>
                <div className="flex items-end gap-0.5 h-10">
                  {Array.from({ length: 30 }, (_, i) => {
                    const d = new Date(Date.now() - (29 - i) * 86400000).toISOString().split('T')[0];
                    const session = goals.history.find(s => s.date === d);
                    const words = session?.words || 0;
                    const maxWords = Math.max(...goals.history.map(s => s.words || 0), 1);
                    const height = Math.max(2, (words / maxWords) * 100);
                    const metGoal = goals.daily_goal > 0 && words >= goals.daily_goal;
                    return (
                      <div
                        key={d}
                        className={`flex-1 rounded-sm transition-colors ${metGoal ? 'bg-green-400' : words > 0 ? 'bg-brand-accent/60' : 'bg-surface-alt'}`}
                        style={{ height: `${height}%` }}
                        title={`${d}: ${words} words`}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-text-muted mb-3">Set a word count goal to track your writing progress</p>
            <Button onClick={() => setEditing(true)} variant="outline" className="gap-2" data-testid="set-goals-btn">
              <Target className="w-4 h-4" />
              Set Goals
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WritingGoals;
